/**
 * Thrown trident model (vanilla TridentModel on entity/trident.png, 32x32):
 * pole 1x25x1 at uv (0,6), base 3x2x1 at (4,0), left/right spikes 1x4x1 at
 * (4,3) / (4,3) mirrored, middle spike 1x4x1 at (0,0). Drawn pointing along
 * its flight direction (the prongs lead), lit by the world light.
 */

import * as THREE from 'three';
import { boxGeometry } from './firstPerson';
import type { TridentView } from '../entities/trident';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';

export class TridentRenderer {
  private readonly group = new THREE.Group();
  private readonly geo: THREE.BufferGeometry;
  private readonly mat: THREE.MeshBasicMaterial;
  private readonly meshes: THREE.Mesh[] = [];

  constructor(scene: THREE.Scene) {
    const tex = new THREE.TextureLoader().load('mc/entity/trident/trident.png');
    tex.magFilter = tex.minFilter = THREE.NearestFilter;
    tex.colorSpace = THREE.SRGBColorSpace;
    tex.generateMipmaps = false;
    this.mat = new THREE.MeshBasicMaterial({ map: tex, alphaTest: 0.1, side: THREE.DoubleSide });
    // vanilla parts (y down, px): pole (-0.5,2,-0.5) 1x25x1, base (-1.5,0,-0.5) 3x2x1,
    // left spike (-2.5,-3,-0.5) 1x4x1, middle (-0.5,-4,-0.5) 1x4x1, right (1.5,-3,-0.5) 1x4x1
    const parts = [
      boxGeometry(0, 6, -0.5, 2, -0.5, 1, 25, 1, 0, 32, 32),
      boxGeometry(4, 0, -1.5, 0, -0.5, 3, 2, 1, 0, 32, 32),
      boxGeometry(4, 3, -2.5, -3, -0.5, 1, 4, 1, 0, 32, 32),
      boxGeometry(0, 0, -0.5, -4, -0.5, 1, 4, 1, 0, 32, 32),
      boxGeometry(4, 3, 1.5, -3, -0.5, 1, 4, 1, 0, 32, 32),
    ];
    const merged = mergeGeometries(parts.map((g) => g.index ? g.toNonIndexed() : g))!;
    // boxGeometry already returns blocks (px / 16); prongs (y < 0, model-up) point along -y: rotate so they lead along -z
    merged.translate(0, -11 / 16, 0);
    merged.rotateX(Math.PI / 2); // -y (prongs) -> -z (forward)
    this.geo = merged;
    scene.add(this.group);
  }

  update(views: readonly TridentView[], alpha: number): void {
    while (this.meshes.length < views.length) {
      const m = new THREE.Mesh(this.geo, this.mat);
      m.frustumCulled = false;
      this.group.add(m);
      this.meshes.push(m);
    }
    this.meshes.forEach((m, i) => {
      const v = views[i];
      m.visible = !!v;
      if (!v) return;
      m.position.set(v.px + (v.x - v.px) * alpha, v.py + (v.y - v.py) * alpha, v.pz + (v.z - v.pz) * alpha);
      m.rotation.set(0, 0, 0);
      m.rotation.order = 'YXZ';
      m.rotation.y = v.yaw;
      m.rotation.x = v.pitch;
      const l = 0.25 + 0.75 * Math.max(0, Math.min(1, v.light));
      this.mat.color.setRGB(l, l, l);
    });
  }
}
